import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { StripeProvider } from '../../contexts/StripeContext';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import marketplaceService from '../../services/marketplaceService';
import ActiveListings from './components/ActiveListings';
import MyListings from './components/MyListings';
import MyOffers from './components/MyOffers';
import ActiveTransactions from './components/ActiveTransactions';
import TradeHistory from './components/TradeHistory';
import CreateListingModal from './components/CreateListingModal';
import ListingDetailModal from './components/ListingDetailModal';
import ActiveAuctions from './components/ActiveAuctions';
import CreateAuctionModal from './components/CreateAuctionModal';
import AuctionDetailModal from './components/AuctionDetailModal';
import MyAuctions from './components/MyAuctions';
import ListingTypeSelector from './components/ListingTypeSelector';

const MarketplaceDashboard = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('browse');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateAuctionModal, setShowCreateAuctionModal] = useState(false);
  const [showListingTypeSelector, setShowListingTypeSelector] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [stats, setStats] = useState({
    activeListings: 0,
    pendingOffers: 0,
    activeTransactions: 0,
    completedTrades: 0,
    activeAuctions: 0,
    totalBids: 0
  });
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadDashboardStats();
  }, [user, navigate]);

  const loadDashboardStats = async () => {
    setLoading(true);
    try {
      const [listingsRes, offersRes, transactionsRes, historyRes, auctionsRes, bidsRes] = await Promise.all([
        marketplaceService?.getUserListings(user?.id, 'active'),
        marketplaceService?.getUserOffers(user?.id),
        marketplaceService?.getUserTransactions(user?.id),
        marketplaceService?.getTradeHistory(user?.id),
        marketplaceService?.getUserAuctions(user?.id, 'active'),
        marketplaceService?.getUserBids(user?.id)
      ]);

      setStats({
        activeListings: listingsRes?.data?.length || 0,
        pendingOffers: offersRes?.data?.filter(o => o?.status === 'pending')?.length || 0,
        activeTransactions: transactionsRes?.data?.filter(t => t?.status === 'held')?.length || 0,
        completedTrades: historyRes?.data?.length || 0,
        activeAuctions: auctionsRes?.data?.length || 0,
        totalBids: bidsRes?.data?.length || 0
      });
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'browse', label: 'Browse Marketplace', icon: 'Store' },
    { id: 'auctions', label: 'Browse Auctions', icon: 'Gavel' },
    { id: 'my-listings', label: 'My Listings', icon: 'Package' },
    { id: 'my-auctions', label: 'My Auctions', icon: 'Hammer' },
    { id: 'my-offers', label: 'My Offers', icon: 'MessageSquare' },
    { id: 'transactions', label: 'Active Transactions', icon: 'DollarSign' },
    { id: 'history', label: 'Trade History', icon: 'History' }
  ];

  const statCards = [
    { label: 'Active Listings', value: stats?.activeListings, icon: 'Package', color: 'text-blue-500' },
    { label: 'Active Auctions', value: stats?.activeAuctions, icon: 'Gavel', color: 'text-purple-500' },
    { label: 'Pending Offers', value: stats?.pendingOffers, icon: 'MessageSquare', color: 'text-yellow-500' },
    { label: 'My Bids', value: stats?.totalBids, icon: 'TrendingUp', color: 'text-green-500' }
  ];

  return (
    <StripeProvider>
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Trading Marketplace | Crazy Rare Club</title>
          <meta name="description" content="Buy, sell, and auction rare collectibles with secure escrow-protected transactions on Crazy Rare Club — the premier collectibles marketplace." />
          <meta name="keywords" content="collectibles marketplace, rare collectibles, buy sell collectibles, auction collectibles, trading cards, sports cards, vintage toys" />
          <link rel="canonical" href="https://crazyrareclub.com/marketplace" />

          {/* Open Graph */}
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Crazy Rare Club" />
          <meta property="og:title" content="Trading Marketplace | Crazy Rare Club" />
          <meta property="og:description" content="Buy, sell, and auction rare collectibles with secure escrow-protected transactions on Crazy Rare Club." />
          <meta property="og:url" content="https://crazyrareclub.com/marketplace" />
          <meta property="og:image" content="https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png" />
          <meta property="og:image:alt" content="Crazy Rare Club Marketplace — Buy and sell rare collectibles" />

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Trading Marketplace | Crazy Rare Club" />
          <meta name="twitter:description" content="Buy, sell, and auction rare collectibles with secure escrow-protected transactions on Crazy Rare Club." />
          <meta name="twitter:image" content="https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png" />

          {/* JSON-LD Structured Data */}
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Trading Marketplace",
            "description": "Buy, sell, and auction rare collectibles with secure escrow-protected transactions on Crazy Rare Club.",
            "url": "https://crazyrareclub.com/marketplace",
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://crazyrareclub.com" },
                { "@type": "ListItem", "position": 2, "name": "Marketplace", "item": "https://crazyrareclub.com/marketplace" }
              ]
            },
            "publisher": {
              "@type": "Organization",
              "name": "Crazy Rare Club",
              "url": "https://crazyrareclub.com",
              "logo": {
                "@type": "ImageObject",
                "url": "https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png"
              }
            },
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://crazyrareclub.com/marketplace?search={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}</script>
        </Helmet>
        <Header />
        <div className="flex">
          <Sidebar onToggle={() => setSidebarOpen(!sidebarOpen)} />
          <main className="flex-1 p-4 sm:p-6 lg:ml-64 min-w-0">
            {/* Page Header */}
            <div className="mb-6 sm:mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-1 sm:mb-2">Trading Marketplace</h1>
                  <p className="text-muted-foreground text-sm sm:text-base">
                    Buy, sell, and auction collectibles with secure escrow-protected transactions
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => setShowListingTypeSelector(true)}
                    iconName="Tag"
                    iconPosition="left"
                    size="md"
                  >
                    List an Item
                  </Button>
                  <Button
                    onClick={() => setShowCreateModal(true)}
                    iconName="Plus"
                    iconPosition="left"
                    size="md"
                    variant="outline"
                  >
                    Quick Listing
                  </Button>
                  <Button
                    onClick={() => setShowCreateAuctionModal(true)}
                    iconName="Gavel"
                    iconPosition="left"
                    size="md"
                    variant="outline"
                  >
                    Quick Auction
                  </Button>
                </div>
              </div>

              {/* Stats Cards */}
              {loading ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 animate-pulse">
                  {[1, 2, 3, 4]?.map(i => (
                    <div key={i} className="bg-card border border-border rounded-lg p-4 sm:p-6 h-20 sm:h-24" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  {statCards?.map((stat, index) => (
                    <div key={index} className="bg-card border border-border rounded-lg p-4 sm:p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs sm:text-sm text-muted-foreground mb-1">{stat?.label}</p>
                          <p className="text-2xl sm:text-3xl font-bold text-foreground">{stat?.value}</p>
                        </div>
                        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-muted/20 flex items-center justify-center ${stat?.color}`}>
                          <Icon name={stat?.icon} size={20} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="mb-6">
              <div className="border-b border-border">
                <div className="flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide pb-px">
                  {tabs?.map(tab => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center space-x-1.5 sm:space-x-2 pb-3 sm:pb-4 border-b-2 transition-colors whitespace-nowrap text-sm min-h-[44px] ${
                        activeTab === tab?.id
                          ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} />
                      <span className="font-medium">{tab?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Tab Content */}
            <div className="mt-4 sm:mt-6">
              {activeTab === 'browse' && (
                <ActiveListings onSelectListing={setSelectedListing} />
              )}
              {activeTab === 'auctions' && (
                <ActiveAuctions onSelectAuction={setSelectedAuction} />
              )}
              {activeTab === 'my-listings' && (
                <MyListings onRefresh={loadDashboardStats} />
              )}
              {activeTab === 'my-auctions' && (
                <MyAuctions onRefresh={loadDashboardStats} />
              )}
              {activeTab === 'my-offers' && (
                <MyOffers onRefresh={loadDashboardStats} />
              )}
              {activeTab === 'transactions' && (
                <ActiveTransactions onRefresh={loadDashboardStats} />
              )}
              {activeTab === 'history' && (
                <TradeHistory />
              )}
            </div>
          </main>
        </div>

        {/* Modals */}
        {showListingTypeSelector && (
          <ListingTypeSelector
            isOpen={showListingTypeSelector}
            onClose={() => setShowListingTypeSelector(false)}
            onSuccess={(type) => {
              setShowListingTypeSelector(false);
              loadDashboardStats();
              setActiveTab(type === 'auction' ? 'my-auctions' : 'my-listings');
            }}
          />
        )}

        {showCreateModal && (
          <CreateListingModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              loadDashboardStats();
              setActiveTab('my-listings');
            }}
          />
        )}

        {showCreateAuctionModal && (
          <CreateAuctionModal
            isOpen={showCreateAuctionModal}
            onClose={() => setShowCreateAuctionModal(false)}
            onSuccess={() => {
              setShowCreateAuctionModal(false);
              loadDashboardStats();
              setActiveTab('my-auctions');
            }}
          />
        )}

        {selectedListing && (
          <ListingDetailModal
            listing={selectedListing}
            onClose={() => setSelectedListing(null)}
            onOfferSubmitted={() => {
              setSelectedListing(null);
              loadDashboardStats();
              setActiveTab('my-offers');
            }}
          />
        )}

        {selectedAuction && (
          <AuctionDetailModal
            auction={selectedAuction}
            onClose={() => setSelectedAuction(null)}
            onBidPlaced={() => {
              setSelectedAuction(null);
              loadDashboardStats();
            }}
          />
        )}
      </div>
    </StripeProvider>
  );
};

export default MarketplaceDashboard;
